import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  Flex,
  PrimaryButton,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { Modal } from '~/components/common/Modal'

type Props = {
  isOpen: boolean
  /** キャンセル・閉じる。呼び出し元 (承認フロー設定モーダル) の編集内容は保持する。 */
  onClose: () => void
  /** 選択された反映先エリアを渡す。実行後に閉じるのは呼び出し元の責務。 */
  onReflect: (destAreas: AreaCode[]) => void
  /** 反映元エリアの表示名 (「反映元: 東京」)。選択肢には出さない。 */
  sourceAreaLabel: string
  /** 反映先の候補 (= 反映元を除いた全エリア)。 */
  destAreas: readonly AreaCode[]
  areaLabels: Readonly<Record<AreaCode, string>>
}

/**
 * 反映先エリア選択モーダル (spec AC-APF-023 / issue #7158)。
 *
 * 承認フロー設定モーダルの「この設定を他エリアにも反映」から開く**専用モーダル**。
 * 8080 `admin/approval-flow.html:420` の `#apr-copy-modal` に対応する。
 *
 * ## なぜ専用モーダルにするか
 *
 * 旧実装は承認フロー設定モーダルの**本文内に展開パネル**を差し込んでいた。段階カードの
 * 上に可変高さのパネルが割り込むため、段階を編集していた位置が押し下げられ、
 * 「どこを編集していたか」を見失う。選択は独立した意思決定なので面を分ける。
 *
 * ## 文言は「コピー」でなく「反映」
 *
 * 8080 は一貫して「反映」で書かれている (`反映先のエリアを選択` / `反映元` / `反映`)。
 * spec `AC-APF-023` の本文だけが「コピー」のままなので、**spec 側の文言更新を
 * 別 issue で PdM に依頼している**。UI は 8080 を正とする。
 *
 * ## 初期選択
 *
 * 開くたびに**反映先を全選択**した状態に戻す (issue #7158 の受け入れ条件)。
 * 前回の選択を持ち越すと、別の業務処理・別エリアを開いた時に意図しない反映先が
 * 残る (8080 も `closeCopyModal(false)` で開くたびに作り直している)。
 */
export const ReflectAreasModal = ({
  isOpen,
  onClose,
  onReflect,
  sourceAreaLabel,
  destAreas,
  areaLabels,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [selectedAreas, setSelectedAreas] = useState<ReadonlySet<AreaCode>>(
    () => new Set<AreaCode>(),
  )

  /**
   * `destAreas` は呼び出し元が **毎 render 新しい配列**を作って渡す
   * (`ApprovalMatrixModalHost` が `reflectAreas={{ areas: ... }}` をオブジェクト
   * リテラルで組み、`StageEditModal` の `useMemo` がそれを deps にしているため)。
   * そのまま effect の deps に置くと、**モーダルを開いている間に親が再描画される
   * だけで全選択に戻り、ユーザーが外したチェックが復活する** (レビュー指摘)。
   * 初期化は「開いた瞬間」だけにしたいので、値は ref 経由で読む。
   */
  const destAreasRef = useRef(destAreas)
  destAreasRef.current = destAreas

  // 開いた時だけ全選択へ戻す (前回の選択を持ち越さない)。
  useEffect(() => {
    if (isOpen) {
      setSelectedAreas(new Set(destAreasRef.current))
    }
  }, [isOpen])

  const toggleArea = useCallback((area: AreaCode) => {
    setSelectedAreas((prev) => {
      const next = new Set(prev)
      if (next.has(area)) next.delete(area)
      else next.add(area)
      return next
    })
  }, [])

  const isAllSelected = useMemo(
    () => destAreas.length > 0 && destAreas.every((a) => selectedAreas.has(a)),
    [destAreas, selectedAreas],
  )

  /**
   * 全選択 / 全解除の切替 (design `apr-copy-toggle-all`)。
   * エリアは 9〜10 件あり、 全件に反映したい時に 1 件ずつ押させない。
   */
  const toggleAll = useCallback(() => {
    setSelectedAreas(isAllSelected ? new Set() : new Set(destAreas))
  }, [destAreas, isAllSelected])

  const handleReflect = useCallback(() => {
    if (selectedAreas.size === 0) return
    onReflect([...selectedAreas])
  }, [onReflect, selectedAreas])

  const isEmpty = selectedAreas.size === 0

  return (
    <Modal
      footer={
        <Flex alignItems="center" justifyContent="flex-end" width="100%">
          {/* 選択件数 (design `apr-copy-hint`)。 押す前に「何件に反映されるか」が
              分かるようにする。
              寸法・色は 8080 の `.apr-copy-panel-hint`
              (`font-size: var(--font-size-md)` / `color: var(--text-high-emphasis)`)
              に合わせる。 ここは「今どうなっているか」を伝える本文相当の行で、
              補助的な注記ではない。 */}
          <Box marginRight="auto" color="Text.highEmphasis" fontSize="md">
            {isEmpty
              ? t('page:adminApprovalFlow.reflectAreas.empty')
              : t('page:adminApprovalFlow.reflectAreas.count', {
                  count: selectedAreas.size,
                })}
          </Box>
          <TertiaryButton marginRight="8px" onClick={onClose} size="md">
            {t('page:adminApprovalFlow.actions.cancel')}
          </TertiaryButton>
          {/* 無効理由の tooltip / title は hint とは**別の文言**を使う。
              hint は 8080 準拠の状態表示 (「反映先が未選択です」) だが、 押せない
              ボタンに出すのは「何をすれば押せるか」の方が有効なため、 指示形の
              `emptyTooltip` を当てる。 8080 は反映ボタンに tooltip を持たないので
              design 側に対応する文言は無い。 */}
          <ButtonTooltip
            content={
              isEmpty
                ? t('page:adminApprovalFlow.reflectAreas.emptyTooltip')
                : undefined
            }
          >
            <PrimaryButton disabled={isEmpty} onClick={handleReflect} size="md">
              {t('page:adminApprovalFlow.reflectAreas.execute')}
            </PrimaryButton>
          </ButtonTooltip>
        </Flex>
      }
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="md"
      title={t('page:adminApprovalFlow.reflectAreas.title')}
    >
      <Flex flexDirection="column" gap="12px">
        {/* 8080 `.apr-copy-source` は
            `font-size: var(--font-size-md)` / `color: var(--text-high-emphasis)`。
            どこから反映するのかはこのモーダルの前提情報なので、 補助的な注記の色に
            落とさない。 */}
        <Box color="Text.highEmphasis" fontSize="md">
          {t('page:adminApprovalFlow.reflectAreas.source', {
            area: sourceAreaLabel,
          })}
        </Box>
        <Flex justifyContent="flex-start">
          <TertiaryButton onClick={toggleAll} size="sm">
            {isAllSelected
              ? t('page:adminApprovalFlow.reflectAreas.clearAll')
              : t('page:adminApprovalFlow.reflectAreas.selectAll')}
          </TertiaryButton>
        </Flex>
        <Flex flexWrap="wrap" gap="8px 16px">
          {destAreas.map((area) => (
            <Checkbox
              key={area}
              checked={selectedAreas.has(area)}
              onCheckedChange={() => {
                toggleArea(area)
              }}
            >
              {areaLabels[area]}
            </Checkbox>
          ))}
        </Flex>
      </Flex>
    </Modal>
  )
}
